import { apiClient } from "@/api/api-client";
import FiledVsClosedChart from "@/components/charts/filed-vs-closed-chart";
import LoginHeatmap from "@/components/charts/login-heatmap";
import FloatingAction from "@/components/core/actions/floating-action";
import { Button } from "@/components/core/buttons/button";
import { useModal } from "@/components/core/modals/use-modal";
import { Content } from "@/components/layouts/content";
import PinCodeModal from "@/features/case-evaluation/components/pin-code-modal";
import IncomingCases from "@/features/case-evaluation/incoming-cases";
import { caseRecordTotalQueryOptions } from "@/features/case-record/api/queries/case-record-statistic-query";
import { caseRecordsQueryOptions } from "@/features/case-record/api/queries/case-records-query";
import CaseStageChart from "@/features/overview/components/case-stage-chart";
import CaseStats from "@/features/overview/components/case-stats";
import StatCard from "@/features/overview/components/stat-card";
import UpcomingEvents from "@/features/overview/components/upcoming-events";
import { useAuth } from "@/hooks/use-auth";
import { IconDownload, IconExternalLink } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import moment from "moment-timezone";
import { u } from "node_modules/framer-motion/dist/types.d-O7VGXDJe";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { useEffect, useState } from "react";
import { components } from "react-select";

export const Route = createFileRoute("/_dashboard/bcc-dashboard")({
	// loader: ({ context: { queryClient }, deps }) => {
	//   return queryClient.ensureQueryData(
	//     caseRecordTotalQueryOptions({}),
	//   );
	// },
	component: RouteComponent,
});

function RouteComponent() {
	const { user } = useAuth();
	const modal = useModal();
	const [totalCases, setTotalCases] = useState(0);
	const [breakdown, setBreakdown] = useState(0);
	const [overdueCount, setOverdueCount] = useState(0);
	const [hearing, setHearings] = useState<{
		preliminary_count: number;
		pretrial_count: number;
		trial_proper_count: number;
		post_judgment_count: number;
	} | null>(null);

	// Get incoming cases data for PDF download
	const incomingCasesQuery = useSuspenseQuery(
		caseRecordsQueryOptions({
			status: "branch_receiving",
		}),
	);
	const incomingCases = incomingCasesQuery.data?.data || [];

	const fetchCaseCount = async () => {
		const response = await apiClient.get("/backoffice/case-records-total");
		setTotalCases(response.data);
	};

	const fetchCaseRecordBreakdown = async () => {
		const response = await apiClient.get("/backoffice/case-records-breakdown");
		setBreakdown(response.data);
	};

	const fetchActionsOverdueCount = async () => {
		const response = await apiClient.get("/backoffice/actions-overdue-count");
		setOverdueCount(response.data);
	};

	const fetchHearingsCount = async () => {
		const ids = user?.branches?.map((branch) => branch?.assignable?.id);
		const year = moment().format("YYYY");
		const response = await apiClient.get(`/backoffice/hearings-per-month?year=${year}&type=month&branch_id=${ids}`);

		if (response?.data) {
			setHearings(response?.data?.data ?? []);
		}
	};

	const openPinCodeModal = () => {
		modal.openModal({
			title: "Update Pin Code",
			description: "Please update your pin code to proceed.",
			size: "sm",
			component: <PinCodeModal />,
		});
	};

	const wrapText = (text: string, maxWidth: number, font: any, fontSize: number) => {
		const words = text.split(" ");
		const lines: string[] = [];
		let currentLine = "";

		for (const word of words) {
			const testLine = currentLine ? `${currentLine} ${word}` : word;
			const testWidth = font.widthOfTextAtSize(testLine, fontSize);

			if (testWidth <= maxWidth) {
				currentLine = testLine;
			} else {
				if (currentLine) {
					lines.push(currentLine);
					currentLine = word;
				} else {
					lines.push(word);
				}
			}
		}

		if (currentLine) {
			lines.push(currentLine);
		}

		return lines;
	};

	const handleDownloadIncomingCasesPDF = async () => {
		if (!incomingCases || incomingCases.length === 0) {
			alert("No incoming cases to download");
			return;
		}

		const pdfDoc = await PDFDocument.create();
		const page = pdfDoc.addPage([595, 842]);
		const { width, height } = page.getSize();
		const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
		const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

		let yPosition = height - 50;

		// Title
		page.drawText("Incoming Cases Report", {
			x: 50,
			y: yPosition,
			size: 18,
			font: boldFont,
			color: rgb(0, 0, 0),
		});

		yPosition -= 25;
		page.drawText(`Generated: ${new Date().toLocaleDateString()}`, {
			x: 50,
			y: yPosition,
			size: 10,
			font: font,
			color: rgb(0.5, 0.5, 0.5),
		});

		yPosition -= 15;
		page.drawText(`Total Cases: ${incomingCases.length}`, {
			x: 50,
			y: yPosition,
			size: 10,
			font: font,
			color: rgb(0.5, 0.5, 0.5),
		});

		yPosition -= 30;

		// Headers
		page.drawText("Case No.", {
			x: 50,
			y: yPosition,
			size: 10,
			font: boldFont,
			color: rgb(0, 0, 0),
		});
		page.drawText("Case Title", {
			x: 130,
			y: yPosition,
			size: 10,
			font: boldFont,
			color: rgb(0, 0, 0),
		});
		page.drawText("Category", {
			x: 350,
			y: yPosition,
			size: 10,
			font: boldFont,
			color: rgb(0, 0, 0),
		});
		page.drawText("Date Filed", {
			x: 450,
			y: yPosition,
			size: 10,
			font: boldFont,
			color: rgb(0, 0, 0),
		});

		yPosition -= 15;
		let currentPage = page;

		for (const caseItem of incomingCases) {
			if (yPosition < 80) {
				currentPage = pdfDoc.addPage([595, 842]);
				yPosition = height - 50;
			}

			// Case Number
			currentPage.drawText(caseItem.case_number || "", {
				x: 50,
				y: yPosition,
				size: 9,
				font: font,
				color: rgb(0, 0, 0),
			});

			// Case Title with text wrapping
			const titleLines = wrapText(caseItem.case_title || "", 200, font, 9);
			let currentTitleY = yPosition;

			for (const line of titleLines) {
				currentPage.drawText(line, {
					x: 130,
					y: currentTitleY,
					size: 9,
					font: font,
					color: rgb(0, 0, 0),
				});
				currentTitleY -= 12;
			}

			// Category with text wrapping
			const categoryLines = wrapText(caseItem.case_category || "", 80, font, 9);
			let currentCategoryY = yPosition;

			for (const line of categoryLines) {
				currentPage.drawText(line, {
					x: 350,
					y: currentCategoryY,
					size: 9,
					font: font,
					color: rgb(0, 0, 0),
				});
				currentCategoryY -= 12;
			}

			// Date Filed
			const filedDate = caseItem.filed_at ? new Date(caseItem.filed_at).toLocaleDateString() : "";
			currentPage.drawText(filedDate, {
				x: 450,
				y: yPosition,
				size: 9,
				font: font,
				color: rgb(0, 0, 0),
			});

			// Calculate the total height used by this row
			const titleHeight = titleLines.length * 12;
			const categoryHeight = categoryLines.length * 12;
			const maxHeight = Math.max(titleHeight, categoryHeight, 12);

			yPosition -= maxHeight + 5;
		}

		const pdfBytes = await pdfDoc.save();
		const blob = new Blob([pdfBytes], { type: "application/pdf" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		const timestamp = new Date().toISOString().split("T")[0];

		link.href = url;
		link.download = `incoming_cases_${timestamp}.pdf`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	useEffect(() => {
		let modalOpened = false;
		if (user?.pin_code_updated_at === null && !modalOpened) {
			openPinCodeModal();
			modalOpened = true;
			console.log("Pin code not updated, opening modal");
		}

		fetchHearingsCount();
		fetchCaseCount();
		fetchCaseRecordBreakdown();
		fetchActionsOverdueCount();
	}, []);

	return (
		<Content>
			<div className="flex flex-col w-full max-w-base p-6 gap-4">
				<div className="flex flex-col w-full">
					<span className="text-xl text-foreground-dark font-semibold">Welcome {user?.first_name},</span>
					<span className="text-base text-foreground/70">View assigned cases and updates</span>
				</div>

				<div className="flex flex-col w-full h-full gap-4">
					{/*<StatCard />*/}

					<div className="flex flex-row gap-4">
						<div className="flex flex-col w-10/12 rounded-md border border-border/50">
							<div className="flex flex-row w-full p-3 border-b border-border/50">
								<span className="text-base font-semibold text-foreground-dark">Hearings for the month of {moment().format("MMMM YYYY")}</span>
							</div>
							<div className="flex flex-col w-full">
								<>
									<div className="flex flex-col w-full">
										<div className="grid grid-cols-4 w-full divide-x divide-border/50">
											<Link
												to={"/calendar"}
												className="group relative flex flex-col items-center justify-center p-6 min-h-[140px] bg-background hover:bg-slate-50"
											>
												<div className="absolute top-2 right-2 text-foreground/30 opacity-0 group-hover:opacity-100">
													<IconExternalLink size={18} stroke={2} />
												</div>
												<span className="text-3xl font-bold text-foreground">{hearing?.preliminary_count}</span>
												<span className="text-base font-medium text-muted-foreground">Preliminary</span>
											</Link>
											<Link
												to={"/calendar"}
												className="group relative flex flex-col items-center justify-center p-6 min-h-[140px] bg-background hover:bg-slate-50"
											>
												<div className="absolute top-2 right-2 text-foreground/30 opacity-0 group-hover:opacity-100">
													<IconExternalLink size={18} stroke={2} />
												</div>
												<span className="text-3xl font-bold text-foreground">{hearing?.pretrial_count}</span>
												<span className="text-base font-medium text-muted-foreground">Pre-Trial</span>
											</Link>
											<Link
												to={"/calendar"}
												className="group relative flex flex-col items-center justify-center p-6 min-h-[140px] bg-background hover:bg-slate-50"
											>
												<div className="absolute top-2 right-2 text-foreground/30 opacity-0 group-hover:opacity-100">
													<IconExternalLink size={18} stroke={2} />
												</div>
												<span className="text-3xl font-bold text-foreground">{hearing?.trial_proper_count}</span>
												<span className="text-base font-medium text-muted-foreground">Trial Proper</span>
											</Link>
											<Link
												to={"/calendar"}
												className="group relative flex flex-col items-center justify-center p-6 min-h-[140px] bg-background hover:bg-slate-50"
											>
												<div className="absolute top-2 right-2 text-foreground/30 opacity-0 group-hover:opacity-100">
													<IconExternalLink size={18} stroke={2} />
												</div>
												<span className="text-3xl font-bold text-foreground">{hearing?.post_judgment_count}</span>
												<span className="text-base font-medium text-muted-foreground">Post Judgement</span>
											</Link>
											{/* <div className="flex flex-col items-center justify-center p-6 min-h-[120px] rounded-xl border border-border/50 bg-muted/30 shadow-md">
                        <span className="text-3xl font-bold text-foreground">{0}</span>
                        <span className="text-base font-medium text-muted-foreground">Total Hearings</span>
                      </div> */}
										</div>
									</div>

									{/* <DataTable data={cases} columns={columns} /> */}
									{/* <Pagination
                      onClick={handlePageChange}
                      initialPage={pagination.current_page}
                      pageCount={pagination.last_page}
                    /> */}
								</>
							</div>
						</div>

						<div className="flex shrink-0 w-[280px] flex-col rounded-md border border-border/50">
							<div className="flex flex-row w-full p-3 border-b border-border/50">
								<span className="text-base font-semibold text-foreground-dark">Login Frequency ({moment().format("MMM")})</span>
							</div>
							<div className="flex flex-col w-full">
								<LoginHeatmap />
							</div>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-4">
						<div className="col-span-12">
							<div className="flex flex-col w-full gap-4">
								<div className="flex flex-col w-full h-auto rounded-md border border-border/50 overflow-hidden">
									<div className="flex flex-row w-full p-3 border-b border-border/50">
										<span className="text-base font-semibold text-foreground-dark">Total Cases ({totalCases})</span>
									</div>
									<div className="flex flex-col w-full h-full shrink-0">{breakdown && <CaseStats items={breakdown} />}</div>
								</div>
								{/* <div className="flex flex-col w-full h-full rounded-md border border-border/50 overflow-hidden">
									<div className="flex flex-row w-full p-3 border-b border-border/50">
										<span className="text-base font-semibold text-foreground-dark">
											Filed Cases Comparison
										</span>
									</div>
									<div className="flex flex-col w-full">
										<CaseStageChart />
									</div>
								</div> */}
							</div>
						</div>
						{/*<div className="col-span-4">
							<div className="flex flex-col w-full h-full rounded-md border border-border/50 overflow-hidden">
								<div className="flex flex-row w-full p-3 border-b border-border/50">
									<span className="text-base font-semibold text-foreground-dark">
										Upcoming Events
									</span>
								</div>
								<div className="flex flex-col w-full h-ful overflow-y-auto">
									<UpcomingEvents />
								</div>
							</div>
						</div>*/}
					</div>

					<div className="grid grid-cols-12 w-full gap-4">
						<div className="col-span-12">
							<div className="flex flex-col w-full gap-4">
								<div className="flex flex-col w-full h-auto rounded-md border border-border/50 overflow-hidden">
									<div className="flex flex-row w-full p-3 border-b border-border/50">
										<span className="text-base font-semibold text-foreground-dark">Filed vs Closed Cases ({moment().format("YYYY")})</span>
									</div>
									<div className="flex flex-col w-full h-full shrink-0">
										<FiledVsClosedChart />
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="flex flex-row gap-4">
						<div className="flex flex-col w-full rounded-md border border-border/50">
							<div className="flex flex-row w-full p-3 border-b border-border/50">
								<span className="text-base font-semibold text-foreground-dark">Overdue Compliance as of {moment().format("MMMM DD, YYYY")}</span>
							</div>
							<div className="flex flex-col w-full">
								<>
									<div className="flex flex-col w-full">
										<div className="grid grid-cols-4 w-full divide-x divide-border/50">
											<div className="group relative flex flex-col items-center justify-center p-6 min-h-[140px] bg-background hover:bg-slate-50">
												<span className="text-3xl font-bold text-foreground">{overdueCount}</span>
												<span className="text-base font-medium text-muted-foreground">Total</span>
											</div>
										</div>
									</div>

									{/* <DataTable data={cases} columns={columns} /> */}
									{/* <Pagination
                      onClick={handlePageChange}
                      initialPage={pagination.current_page}
                      pageCount={pagination.last_page}
                    /> */}
								</>
							</div>
						</div>
					</div>

					<div className="flex flex-col w-full h-full rounded-md border border-border/50 overflow-hidden">
						<div className="flex flex-row w-full p-3 border-b border-border/50 justify-between items-center">
							<span className="text-base font-semibold text-foreground-dark">Incoming Cases</span>
							<Button
								onClick={handleDownloadIncomingCasesPDF}
								variant="outline"
								size="sm"
								className="inline-flex items-center gap-1"
								disabled={!incomingCases || incomingCases.length === 0}
							>
								<IconDownload size={16} stroke={1.5} />
								Download PDF
							</Button>
						</div>
						<div className="flex flex-col w-full">
							<IncomingCases />
						</div>
					</div>
				</div>
				{/* <FloatingAction
					title="Quick Actions"
					description="Choose an action below"
					actions={[
						{
							label: "Receive this case",
							variant: "green",
							onClick: () => alert("Submit clicked"),
						},
					]}
				/> */}
			</div>
		</Content>
	);
}
